import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';
import './LegalPages.css';

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Terms of Service"
        description="Read Hangrily's terms of service. Understand the rules and conditions for using our restaurant decision app."
        path="/terms"
      />
      <PublicHeader />

      <main className="legal-content">
        <div className="legal-container">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: March 2, 2026</p>

          <section className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              Welcome to Hangrily. By accessing or using our web application, mobile applications, or any related services (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Hangrily ("we," "us," or "our"). We reserve the right to modify these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Description of Service</h2>
            <p>
              Hangrily is a restaurant decision-making platform that helps individuals, couples, and groups choose where to eat through a Tinder-style swiping interface. The Service includes:
            </p>
            <ul>
              <li>Restaurant search and recommendations from verified data sources</li>
              <li>Solo, Couple, and Group decision-making modes</li>
              <li>Partner linking and session sharing</li>
              <li>Decision history and analytics</li>
              <li>User profiles and preferences</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Eligibility</h2>
            <p>You must meet the following requirements to use the Service:</p>
            <ul>
              <li>Be at least 13 years of age (or the age of majority in your jurisdiction)</li>
              <li>Have the legal capacity to enter into a binding contract</li>
              <li>Not be prohibited from using the Service under applicable laws</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. User Accounts</h2>
            
            <h3>4.1 Account Creation</h3>
            <p>
              To use certain features of the Service, you must create an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activities under your account</li>
            </ul>

            <h3>4.2 Account Termination</h3>
            <p>
              You may delete your account at any time from your profile settings. We reserve the right to suspend or terminate your account if you violate these Terms or engage in prohibited conduct.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Acceptable Use</h2>
            
            <h3>5.1 You Agree NOT To:</h3>
            <ul>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Impersonate any person or entity</li>
              <li>Upload viruses, malware, or malicious code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Scrape, crawl, or data mine the Service</li>
              <li>Reverse engineer or decompile any part of the Service</li>
              <li>Use automated systems (bots) without permission</li>
              <li>Interfere with the proper functioning of the Service</li>
              <li>Create multiple accounts to abuse features</li>
              <li>Share your account credentials with others</li>
            </ul>

            <h3>5.2 Consequences of Violation</h3>
            <p>
              Violation of these acceptable use policies may result in immediate account suspension or termination, and we may report illegal activities to law enforcement.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Intellectual Property</h2>
            
            <h3>6.1 Our Rights</h3>
            <p>
              All content, features, and functionality of the Service, including but not limited to text, graphics, logos, icons, images, audio clips, software, and the compilation thereof (collectively, "Content"), are the exclusive property of Hangrily and are protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3>6.2 Limited License</h3>
            <p>
              We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes. You may not:
            </p>
            <ul>
              <li>Copy, modify, or create derivative works of the Service</li>
              <li>Distribute, sell, or sublicense the Service</li>
              <li>Remove any copyright or proprietary notices</li>
              <li>Use our trademarks without written permission</li>
            </ul>

            <h3>6.3 User Content</h3>
            <p>
              You retain ownership of any content you submit (e.g., profile information, preferences). By using the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, store, and display your content solely to provide the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Third-Party Services</h2>
            <p>
              The Service integrates with third-party services, including:
            </p>
            <ul>
              <li><strong>Restaurant Data Providers:</strong> Real-time restaurant information, ratings, and photos</li>
              <li><strong>Mapping Services:</strong> Directions and location services</li>
              <li><strong>Supabase:</strong> Database and authentication services</li>
            </ul>
            <p>
              Your use of these third-party services is subject to their respective terms of service and privacy policies. We are not responsible for the availability, accuracy, or content of third-party services.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Disclaimers and Limitations of Liability</h2>
            
            <h3>8.1 Service "As Is"</h3>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>

            <h3>8.2 No Guarantee of Accuracy</h3>
            <p>
              We do not guarantee the accuracy, completeness, or timeliness of restaurant information provided through the Service. Restaurant data is sourced from third-party providers and may be outdated or incorrect.
            </p>

            <h3>8.3 Limitation of Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, HANGRILY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
            </p>
            <ul>
              <li>Your use or inability to use the Service</li>
              <li>Unauthorized access to your account or data</li>
              <li>Errors, bugs, or interruptions in the Service</li>
              <li>Third-party content or conduct</li>
              <li>Any other matter relating to the Service</li>
            </ul>
            <p>
              IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED $100 USD OR THE AMOUNT YOU PAID US IN THE PAST 12 MONTHS, WHICHEVER IS GREATER.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Hangrily, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or in any way connected with:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your content or conduct on the Service</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>10. Privacy</h2>
            <p>
              Your use of the Service is also governed by our <Link to="/privacy">Privacy Policy</Link>, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your information.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Modifications to the Service</h2>
            <p>
              We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including but not limited to:
            </p>
            <ul>
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Requests by law enforcement</li>
              <li>Extended periods of inactivity</li>
              <li>Technical or security issues</li>
            </ul>
            <p>
              Upon termination, your right to use the Service will immediately cease, and we may delete your account and data.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Dispute Resolution</h2>
            
            <h3>13.1 Informal Resolution</h3>
            <p>
              Before filing a claim, you agree to contact us at <a href="mailto:legal@hangrily.app">legal@hangrily.app</a> and attempt to resolve the dispute informally for at least 30 days.
            </p>

            <h3>13.2 Binding Arbitration</h3>
            <p>
              Any dispute arising out of or relating to these Terms or the Service shall be resolved through binding arbitration in accordance with the American Arbitration Association's rules, rather than in court, except that you may assert claims in small claims court if your claims qualify.
            </p>

            <h3>13.3 Class Action Waiver</h3>
            <p>
              YOU AGREE THAT ANY ARBITRATION OR PROCEEDING SHALL BE LIMITED TO THE DISPUTE BETWEEN YOU AND HANGRILY INDIVIDUALLY. YOU WAIVE YOUR RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions. Any legal action or proceeding shall be brought exclusively in the federal or state courts located in Delaware.
            </p>
          </section>

          <section className="legal-section">
            <h2>15. Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
            </p>
          </section>

          <section className="legal-section">
            <h2>16. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Hangrily regarding the Service and supersede all prior agreements and understandings.
            </p>
          </section>

          <section className="legal-section">
            <h2>17. Contact Information</h2>
            <p>If you have questions about these Terms, please contact us:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> <a href="mailto:legal@hangrily.app">legal@hangrily.app</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@hangrily.app">support@hangrily.app</a></p>
              <p><strong>Website:</strong> <a href="https://hangrily.app">https://hangrily.app</a></p>
            </div>
          </section>

          <div className="highlight-box">
            <h3>Summary (Not Legally Binding)</h3>
            <p>
              In plain English: Use Hangrily responsibly. Don't be a jerk. We provide the service "as is" and aren't responsible if a restaurant is closed or the food is bad. We respect your privacy and won't sell your data. If you have issues, contact us first before taking legal action. By using Hangrily, you agree to these terms.
            </p>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
